import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Plus, Pencil, Trash2, Check, Star, X, Home, Briefcase, Truck, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import { useCounties } from '@/hooks/useCounties';
import { US_STATES, stateNameToCode } from '@/constants/usStates';

const LABELS = [
  { value: 'mailing',  label: 'Mailing',     icon: FileText, color: 'bg-blue-100 text-blue-700' },
  { value: 'job_site', label: 'Job Site',    icon: Briefcase, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'billing',  label: 'Billing',     icon: Home, color: 'bg-amber-100 text-amber-700' },
  { value: 'shipping', label: 'Shipping',    icon: Truck, color: 'bg-fuchsia-100 text-fuchsia-700' },
  { value: 'other',    label: 'Other',       icon: MapPin, color: 'bg-gray-100 text-gray-700' },
];

const emptyAddr = () => ({
  label: 'job_site',
  address: '',
  city: '',
  county: '',
  state: 'FL',
  zip: '',
  parcel_id: '',
  notes: '',
  is_primary: false,
});

/**
 * ManageAddresses — modal that lets a user CRUD multiple addresses on a contact.
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - contactId: string
 *   - contactName?: string  (for the header)
 *   - onChanged?: () => void  (called after any change so parent can refresh)
 */
export default function ManageAddresses({ open, onClose, contactId, contactName = '', onChanged }) {
  const counties = useCounties();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyAddr());

  const load = useCallback(async () => {
    if (!open || !contactId) return;
    setLoading(true);
    try {
      const res = await api.get(`/contacts/${contactId}/addresses`);
      setAddresses(res.data || []);
    } catch {
      toast.error('Failed to load addresses');
    } finally {
      setLoading(false);
    }
  }, [open, contactId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyAddr());
    setEditorOpen(true);
  };

  const openEdit = (addr) => {
    setEditing(addr);
    setForm({ ...emptyAddr(), ...addr, state: stateNameToCode(addr.state) || 'FL' });
    setEditorOpen(true);
  };

  const handleAddressPicked = (data) => {
    const isFullSelection = data.lat !== null && data.lat !== undefined;
    setForm(prev => ({
      ...prev,
      address: data.address || prev.address,
      city: data.city || prev.city,
      county: data.county || prev.county,
      state: isFullSelection ? (stateNameToCode(data.state) || prev.state || 'FL') : prev.state,
      zip: data.zip_code || prev.zip,
    }));
  };

  const handleSave = async () => {
    if (!form.address.trim()) { toast.error('Address is required'); return; }
    try {
      if (editing?.id) {
        await api.put(`/contacts/${contactId}/addresses/${editing.id}`, form);
        toast.success('Address updated');
      } else {
        await api.post(`/contacts/${contactId}/addresses`, form);
        toast.success('Address added');
      }
      setEditorOpen(false);
      load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (addressId) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await api.delete(`/contacts/${contactId}/addresses/${addressId}`);
      toast.success('Deleted');
      load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleSetPrimary = async (addr) => {
    try {
      await api.put(`/contacts/${contactId}/addresses/${addr.id}`, { ...addr, is_primary: true });
      load();
      onChanged && onChanged();
      toast.success('Primary address updated');
    } catch (err) {
      toast.error('Failed');
    }
  };

  const getLabelInfo = (key) => LABELS.find(l => l.value === key) || LABELS[4];

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#3399FF]" />
              Manage Addresses {contactName ? `— ${contactName}` : ''}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500">{addresses.length} address{addresses.length === 1 ? '' : 'es'} stored</p>
              <Button size="sm" onClick={openCreate} className="bg-[#003366] hover:bg-[#004080] text-white" data-testid="add-address-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Address
              </Button>
            </div>

            {loading ? (
              <div className="py-12 text-center text-gray-400">Loading...</div>
            ) : addresses.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-10 text-center text-gray-400">
                  <MapPin className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm font-medium">No addresses yet</p>
                  <p className="text-xs mt-1">Click "Add Address" to start.</p>
                </CardContent>
              </Card>
            ) : (
              addresses.map(addr => {
                const li = getLabelInfo(addr.label);
                const Icon = li.icon;
                return (
                  <Card key={addr.id} className="border" data-testid={`address-card-${addr.id}`}>
                    <CardContent className="p-3 flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${li.color} flex-shrink-0`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{li.label}</span>
                          {addr.is_primary && (
                            <span className="inline-flex items-center gap-1 text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">
                              <Star className="w-2.5 h-2.5" /> PRIMARY
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-900 truncate">{addr.address}</p>
                        <p className="text-xs text-gray-500">
                          {[addr.city, addr.state, addr.zip].filter(Boolean).join(', ')}
                          {addr.county ? ` · ${addr.county} County` : ''}
                        </p>
                        {addr.parcel_id && <p className="text-xs text-gray-400 font-mono mt-0.5">Parcel: {addr.parcel_id}</p>}
                        {addr.notes && <p className="text-xs text-gray-500 italic mt-1">{addr.notes}</p>}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {!addr.is_primary && (
                          <Button
                            variant="ghost" size="icon" className="h-7 w-7 text-yellow-500 hover:text-yellow-600"
                            onClick={() => handleSetPrimary(addr)}
                            title="Set as primary"
                            data-testid={`set-primary-${addr.id}`}
                          >
                            <Star className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(addr)} data-testid={`edit-address-${addr.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleDelete(addr.id)} data-testid={`delete-address-${addr.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editor sub-dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-lg">{editing ? 'Edit Address' : 'New Address'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Label</Label>
              <Select value={form.label} onValueChange={v => setForm({...form, label: v})}>
                <SelectTrigger data-testid="address-label-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LABELS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Street Address</Label>
              <AddressAutocomplete
                value={form.address}
                onChange={handleAddressPicked}
                placeholder="Search address..."
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">City</Label>
                <Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} data-testid="addr-city" />
              </div>
              <div>
                <Label className="text-xs">State</Label>
                <Select
                  value={form.state || 'FL'}
                  onValueChange={v => setForm({...form, state: v, county: v === 'FL' ? form.county : ''})}
                >
                  <SelectTrigger data-testid="addr-state-select"><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(s => <SelectItem key={s.code} value={s.code}>{s.code} - {s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">County</Label>
                {form.state === 'FL' ? (
                  <Select value={form.county} onValueChange={v => setForm({...form, county: v})}>
                    <SelectTrigger data-testid="addr-county-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {counties.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={form.county} onChange={e => setForm({...form, county: e.target.value})} data-testid="addr-county-input" />
                )}
              </div>
              <div>
                <Label className="text-xs">Zip</Label>
                <Input value={form.zip} onChange={e => setForm({...form, zip: e.target.value})} data-testid="addr-zip" />
              </div>
            </div>

            <div>
              <Label className="text-xs">Parcel ID</Label>
              <Input value={form.parcel_id} onChange={e => setForm({...form, parcel_id: e.target.value})} placeholder="e.g. 12-34-56-789-000" className="font-mono" />
            </div>

            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} />
            </div>

            <div className="flex items-center justify-between pt-2">
              <Label className="text-xs cursor-pointer">Set as Primary</Label>
              <Switch
                checked={!!form.is_primary}
                onCheckedChange={v => setForm({...form, is_primary: v})}
                data-testid="addr-is-primary"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
            <Button onClick={handleSave} className="bg-[#003366] hover:bg-[#004080] text-white" data-testid="save-address-btn">
              <Check className="w-3.5 h-3.5 mr-1" /> {editing ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
